import type { Vec2 } from "kaplay";
import type { GameConfig } from "../config";
import type { SlotRect } from "../render/background";
import type { Difficulty } from "../systems/difficulty";
import { addAxe, addBoss, type Axe, type Boss } from "./boss";
import { addEmergingZombie, addFallingZombie, addWalkerZombie, type Faller, type Walker, type Zombie } from "./zombie";

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
function clamp01(t: number): number {
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

// Spawner — fills free slots with emerging zombies (capped by the live
// maxZombies), turns a zombie into a WALKER when its crawl timer expires (only
// in stage 2; stage 1 uses crawl = Infinity), recycles slots, and resolves
// shots: walkers nearest->farthest (head then body), then falling zombies
// (anywhere on the body), then emerging. One action per shot.

export interface SpawnerOpts {
  slots: SlotRect[];
  baseY: number; // y at the base of the house where walkers start
  pelletRadius: number;
  difficulty: () => Difficulty; // live tuning (ramps over time, gates stages)
  isPaused: () => boolean;
  windowKillScore: number;
  headshotScore: number;
  bossTuning: GameConfig["boss"]; // boss energy/damage/wave tuning
  onReachPlayer: () => void;
  onLunge?: () => void; // a walker reached the player and started its lunge (for the growl SFX)
  onSpawn?: () => void; // an emerging zombie appeared (for the groan SFX)
  onBossRoar?: () => void; // the boss heaved up out of the ground (for the roar SFX)
  onBossDefeated?: (level: number) => void; // boss energy drained (score reward + banner)
  onAxeThrow?: () => void; // the boss lobbed an axe (for the whoosh SFX)
  onAxeShatter?: () => void; // an axe was shot out of the air (for the clang SFX)
}

// Result of one shot: how many zombies it killed and the raw points (the scene
// doubles points on a multikill).
export interface ShotResult {
  kills: number;
  points: number;
  hit: boolean; // true if it hit/staggered anything (no bullet hole)
}

export interface Spawner {
  resolveHit(p: Vec2): ShotResult;
  triggerBoss(): void; // begin the boss sequence (lull -> rise -> fight)
  bossActive(): boolean; // true from trigger until the boss is defeated
  bossHpFraction(): number; // 0..1 while a boss exists, -1 otherwise
  setTheme(slots: SlotRect[], baseY: number): void; // level swap: clear field + repoint geometry
  debugFill(crawl?: number): void;
  debugWalker(progress?: number): void;
  debugFall(progress?: number): void;
  debugBoss(): void;
}

export function createSpawner(opts: SpawnerOpts): Spawner {
  const occupied = new Set<number>();
  const emerging: Zombie[] = [];
  const walkers: Walker[] = [];
  const fallers: Faller[] = [];
  const axes: Axe[] = []; // boss-thrown projectiles in flight
  // mutable geometry so a level change can repoint the spawner (see setTheme)
  let slots = opts.slots;
  let baseY = opts.baseY;
  let timer = opts.difficulty().spawnInterval;

  // Boss state. "normal" plays the regular spawner; "lull" stops new spawns and
  // waits for the field to clear; "boss" runs an active boss fight (no spawns).
  let phase: "normal" | "lull" | "boss" = "normal";
  let bossLevel = 0; // bosses started so far (drives escalation)
  let lullT = 0; // seconds waited for the field to clear
  let boss: Boss | null = null;

  const mgr = add([]);
  mgr.onUpdate(() => {
    if (opts.isPaused()) return;
    // reap dead walkers + fallers (a faller dies when shot or when it lands)
    for (let i = walkers.length - 1; i >= 0; i--) {
      if (!walkers[i].isAlive()) walkers.splice(i, 1);
    }
    for (let i = fallers.length - 1; i >= 0; i--) {
      if (!fallers[i].isAlive()) fallers.splice(i, 1);
    }
    for (let i = axes.length - 1; i >= 0; i--) {
      if (!axes[i].isAlive()) axes.splice(i, 1);
    }
    if (phase === "normal") {
      timer -= dt();
      if (timer <= 0) {
        const d = opts.difficulty();
        timer = d.spawnInterval;
        trySpawn(d.maxZombies, d.crawlTime);
      }
    } else if (phase === "lull") {
      // spawns are stopped; wait for the field to drain, then raise the boss.
      // A safety cap avoids a deadlock if something lingers.
      lullT += dt();
      const clear = emerging.length === 0 && fallers.length === 0 && walkers.length === 0;
      if (clear || lullT >= 8) startBossNow();
    }
    // phase "boss": no spawns here; the boss entity self-updates and summons.
  });

  function freeSlots(): number[] {
    const free: number[] = [];
    for (let i = 0; i < slots.length; i++) if (!occupied.has(i)) free.push(i);
    return free;
  }

  function removeEmerging(z: Zombie) {
    const i = emerging.indexOf(z);
    if (i >= 0) emerging.splice(i, 1);
    occupied.delete(z.slotIndex);
  }

  // Crawl timer expired. Stage 1: the zombie just retreats/vanishes (slot
  // freed). Stage 2: it falls off the sill (dive -> collapse -> rise) and
  // becomes a walker. The slot is freed immediately either way.
  function onExpire(z: Zombie) {
    const slot = slots[z.slotIndex];
    removeEmerging(z);
    z.destroyNow();
    if (!opts.difficulty().walkersEnabled) return; // stage 1: retreat
    fallers.push(
      addFallingZombie({
        cx: slot.x + slot.w / 2,
        cy: slot.y + slot.h, // start at the windowsill: falls off the window
        groundY: baseY,
        isPaused: opts.isPaused,
        onDone: (x) => spawnWalker(x),
      }),
    );
  }

  function spawnWalker(startX: number, startProgress = 0) {
    walkers.push(
      addWalkerZombie({
        startX,
        startY: baseY,
        traverse: opts.difficulty().walkerTraverse,
        isPaused: opts.isPaused,
        onReachPlayer: opts.onReachPlayer,
        onLunge: opts.onLunge,
        startProgress,
      }),
    );
  }

  // Create the boss now (called from the lull once the field is clear). Scales
  // energy/waves by how many bosses have appeared this run.
  function startBossNow() {
    phase = "boss";
    bossLevel++;
    const b = opts.bossTuning;
    const lvl = bossLevel;
    const escalate = clamp01((lvl - 1) / 4); // ramps over the first few bosses
    boss = addBoss({
      groundY: baseY + 6, // baseY is groundY-6; boss stands in the yard below
      isPaused: opts.isPaused,
      level: lvl,
      maxHp: b.base_hp + b.hp_per_level * (lvl - 1),
      moveSpeed: b.move_speed * (1 + 0.12 * (lvl - 1)), // dodgier each boss
      waveInterval: lerp(b.wave_interval_start, b.wave_interval_min, escalate),
      waveSize: Math.min(4, b.wave_size + (lvl - 1)), // lvl1=2, lvl2=3, lvl3=4, then capped at 4
      waveProgress: b.wave_progress,
      axeIntervalMin: b.axe_interval_min,
      axeIntervalMax: b.axe_interval_max,
      summonWalker: (x, progress) => spawnWalker(x, progress),
      throwAxe: (sx, sy) => {
        const tx = Math.min(width() - 30, Math.max(30, width() / 2 + rand(-60, 60)));
        const flight = Math.max(1.0, b.axe_flight - 0.1 * (lvl - 1)); // faster axes each boss
        // land low (just above the very bottom); the large axe stays visible above the gun
        axes.push(addAxe({ sx, sy, targetX: tx, targetY: height() - 12, flight, isPaused: opts.isPaused, onReachPlayer: opts.onReachPlayer }));
        opts.onAxeThrow?.();
      },
      onDefeated: () => {
        boss = null;
        phase = "normal";
        timer = opts.difficulty().spawnInterval; // resume normal spawns
        axes.splice(0).forEach((ax) => ax.shatter()); // in-flight axes drop harmlessly
        opts.onBossDefeated?.(lvl);
      },
      onRoar: opts.onBossRoar,
    });
  }

  function trySpawn(maxZombies: number, crawl: number) {
    if (emerging.length >= maxZombies) return;
    const free = freeSlots();
    if (free.length === 0) return;
    const idx = free[Math.floor(rand(0, free.length))];
    occupied.add(idx);
    emerging.push(addEmergingZombie(slots[idx], idx, crawl, opts.isPaused, onExpire));
    opts.onSpawn?.();
  }

  return {
    // Multikill: the pellet pierces every zombie at the aim point. Each walker
    // whose head it crosses is killed (headshot), each body it crosses staggers,
    // and every window zombie it overlaps dies. Returns kills + raw points.
    resolveHit(p) {
      const r = opts.pelletRadius;
      let kills = 0;
      let points = 0;
      let hit = false;
      for (const w of walkers) {
        if (!w.isAlive()) continue;
        if (w.headContains(p, r)) {
          w.headshot();
          points += opts.headshotScore;
          kills++;
          hit = true;
        } else if (w.bodyContains(p, r)) {
          w.stagger(p); // body shot: no kill
          hit = true;
        }
      }
      for (const ax of axes) {
        // shoot a thrown axe out of the air for points (not a kill); otherwise
        // it reaches the player and ends the run like a walker.
        if (ax.isAlive() && ax.contains(p, r)) {
          ax.shatter();
          opts.onAxeShatter?.();
          points += opts.bossTuning.axe_score;
          hit = true;
        }
      }
      if (boss && boss.isActive()) {
        // boss dodges any nearby shot, then takes head/body damage (no kill, so
        // it doesn't feed the multikill bonus). Score comes on defeat instead.
        boss.reactToShot(p.x);
        if (boss.headContains(p, r)) {
          boss.damage(opts.bossTuning.head_damage);
          hit = true;
        } else if (boss.bodyContains(p, r)) {
          boss.damage(opts.bossTuning.body_damage);
          hit = true;
        }
      }
      for (const f of fallers) {
        // a falling zombie is shootable anywhere on its body; same score as a
        // walker kill (headshot). Cancels its pending walker spawn.
        if (f.isAlive() && f.contains(p, r)) {
          f.kill();
          points += opts.headshotScore;
          kills++;
          hit = true;
        }
      }
      for (const z of emerging.slice()) {
        // copy: killInSlot/removeEmerging mutate `emerging`
        if (z.isAlive() && z.contains(p, r)) {
          z.killInSlot();
          removeEmerging(z);
          points += opts.windowKillScore;
          kills++;
          hit = true;
        }
      }
      return { kills, points, hit };
    },
    triggerBoss() {
      if (phase !== "normal") return; // one boss at a time
      phase = "lull";
      lullT = 0;
    },
    bossActive: () => phase !== "normal",
    bossHpFraction: () => (boss ? boss.hpFraction() : -1),
    setTheme(newSlots, newBaseY) {
      // clear the field, then repoint geometry to the new level
      for (const z of emerging.slice()) z.destroyNow();
      for (const w of walkers) w.destroyNow();
      for (const f of fallers) f.destroyNow();
      for (const a of axes) a.shatter();
      emerging.length = 0;
      walkers.length = 0;
      fallers.length = 0;
      axes.length = 0;
      occupied.clear();
      slots = newSlots;
      baseY = newBaseY;
      timer = opts.difficulty().spawnInterval;
    },
    debugFill(crawl = 9999) {
      for (const i of freeSlots()) {
        occupied.add(i);
        emerging.push(addEmergingZombie(slots[i], i, crawl, opts.isPaused, onExpire));
      }
    },
    debugWalker(progress = 0) {
      spawnWalker(slots[2].x + slots[2].w / 2, progress);
    },
    debugFall(progress?: number) {
      const slot = slots[0];
      fallers.push(
        addFallingZombie({
          cx: slot.x + slot.w / 2,
          cy: slot.y + slot.h, // start at the windowsill: falls off the window
          groundY: baseY,
          isPaused: opts.isPaused,
          onDone: (x) => spawnWalker(x),
          freezeAt: progress,
        }),
      );
    },
    debugBoss() {
      if (phase === "normal") {
        phase = "lull";
        lullT = 0;
      }
    },
  };
}
